/**
 * Meeting Management Utilities
 * Handles project-level and organization-level meetings
 * Support for related tasks linking and activity feed posting
 */

import { ProjectMeeting, OrganizationMeeting, Task, Project } from '../types';
import { generateId } from './idGenerator';

/**
 * Create a project-level meeting
 */
export function createProjectMeeting(config: {
  organizationId: string;
  projectId: string;
  title: string;
  description?: string;
  scheduledAt: string;
  duration: number; // minutes
  attendeeIds: string[];
  createdBy: string;
  notes?: string;
  relatedTaskIds?: string[];
  postToFeed?: boolean;
  recordingUrl?: string;
}): ProjectMeeting {
  return {
    id: generateId(),
    organizationId: config.organizationId,
    projectId: config.projectId,
    title: config.title,
    description: config.description,
    scheduledAt: config.scheduledAt,
    duration: config.duration,
    attendeeIds: config.attendeeIds,
    notes: config.notes,
    relatedTaskIds: config.relatedTaskIds,
    postToFeed: config.postToFeed ?? true,
    createdAt: new Date().toISOString(),
    createdBy: config.createdBy,
    recordingUrl: config.recordingUrl
  };
}

/**
 * Create an organization-level meeting
 */
export function createOrganizationMeeting(config: {
  organizationId: string;
  title: string;
  description?: string;
  scheduledAt: string;
  duration: number; // minutes
  attendeeIds: string[];
  meetingType: 'administrative' | 'departmental' | 'other';
  createdBy: string;
  notes?: string;
  recordingUrl?: string;
}): OrganizationMeeting {
  return {
    id: generateId(),
    organizationId: config.organizationId,
    title: config.title,
    description: config.description,
    scheduledAt: config.scheduledAt,
    duration: config.duration,
    attendeeIds: config.attendeeIds,
    meetingType: config.meetingType,
    notes: config.notes,
    createdAt: new Date().toISOString(),
    createdBy: config.createdBy,
    recordingUrl: config.recordingUrl
  };
}

/**
 * Update meeting notes and optionally post to project feed
 */
export function updateMeetingNotes(
  meeting: ProjectMeeting,
  notes: string,
  relatedTaskIds?: string[]
): ProjectMeeting {
  return {
    ...meeting,
    notes,
    relatedTaskIds,
    updatedAt: new Date().toISOString()
  };
}

/**
 * Link meeting to tasks by creating comment references
 */
export function linkMeetingToTasks(
  meeting: ProjectMeeting,
  tasks: Task[]
): { task: Task, meetingReference: string }[] {
  if (!meeting.relatedTaskIds || meeting.relatedTaskIds.length === 0) {
    return [];
  }

  return meeting.relatedTaskIds
    .map(taskId => {
      const task = tasks.find(t => t.id === taskId);
      if (!task) return null;

      const meetingReference = `Meeting: ${meeting.title} (${new Date(meeting.scheduledAt).toLocaleDateString()})\n${meeting.notes || ''}`;

      return {
        task,
        meetingReference
      };
    })
    .filter((item): item is { task: Task; meetingReference: string } => item !== null);
}

/**
 * Generate meeting summary markdown
 */
export function generateMeetingSummary(
  meeting: ProjectMeeting,
  attendeeNames: Record<string, string>
): string {
  const attendees = meeting.attendeeIds
    .map(id => attendeeNames[id] || 'Unknown')
    .join(', ');

  const scheduledDate = new Date(meeting.scheduledAt).toLocaleString();

  let summary = `# ${meeting.title}\n\n`;
  summary += `**Date:** ${scheduledDate}\n`;
  summary += `**Duration:** ${meeting.duration} minutes\n`;
  summary += `**Attendees:** ${attendees}\n\n`;

  if (meeting.description) {
    summary += `**Description:** ${meeting.description}\n\n`;
  }

  if (meeting.notes) {
    summary += `## Meeting Notes\n\n${meeting.notes}\n\n`;
  }

  if (meeting.relatedTaskIds && meeting.relatedTaskIds.length > 0) {
    summary += `## Related Tasks\n`;
    meeting.relatedTaskIds.forEach(taskId => {
      summary += `- [Task: ${taskId}]\n`;
    });
    summary += '\n';
  }

  if (meeting.recordingUrl) {
    summary += `## Recording\n[View Recording](${meeting.recordingUrl})\n`;
  }

  return summary;
}

/**
 * Filter meetings by date range
 */
export function filterMeetingsByDateRange(
  meetings: (ProjectMeeting | OrganizationMeeting)[],
  startDate: Date,
  endDate: Date
): (ProjectMeeting | OrganizationMeeting)[] {
  return meetings.filter(meeting => {
    const meetingDate = new Date(meeting.scheduledAt);
    return meetingDate >= startDate && meetingDate <= endDate;
  });
}

/**
 * Get upcoming meetings (within next 30 days)
 */
export function getUpcomingMeetings(
  meetings: (ProjectMeeting | OrganizationMeeting)[]
): (ProjectMeeting | OrganizationMeeting)[] {
  const now = new Date();
  const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

  return filterMeetingsByDateRange(meetings, now, thirtyDaysFromNow)
    .sort((a, b) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime());
}

/**
 * Check if meeting attendee is current user
 */
export function isUserAttending(
  meeting: ProjectMeeting | OrganizationMeeting,
  userId: string
): boolean {
  return meeting.attendeeIds.includes(userId);
}

/**
 * Get meetings for a specific project
 */
export function getProjectMeetings(
  meetings: ProjectMeeting[],
  projectId: string
): ProjectMeeting[] {
  return meetings.filter(m => m.projectId === projectId);
}

/**
 * Get organization-level meetings by type
 */
export function getOrgMeetingsByType(
  meetings: OrganizationMeeting[],
  meetingType: 'administrative' | 'departmental' | 'other'
): OrganizationMeeting[] {
  return meetings.filter(m => m.meetingType === meetingType);
}

/**
 * Format meeting time for display
 */
export function formatMeetingTime(scheduledAt: string, durationMinutes: number): string {
  const startTime = new Date(scheduledAt);
  const endTime = new Date(startTime.getTime() + durationMinutes * 60 * 1000);

  const startStr = startTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  const endStr = endTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

  return `${startStr} - ${endStr}`;
}

/**
 * Check if meeting is upcoming
 */
export function isMeetingUpcoming(scheduledAt: string): boolean {
  return new Date(scheduledAt) > new Date();
}

/**
 * Check if meeting is happening now (within duration)
 */
export function isMeetingNow(scheduledAt: string, durationMinutes: number): boolean {
  const now = new Date();
  const startTime = new Date(scheduledAt);
  const endTime = new Date(startTime.getTime() + durationMinutes * 60 * 1000);

  return now >= startTime && now <= endTime;
}
